import type { CombinedObservation } from "@tellescope/tanstack-query";
import type { VitalConfiguration, VitalConfigurationRange } from "@tellescope/types-models";
import { formatDate, formatTime, resolveClassificationForThresholds, type ObservationType } from "@tellescope/utils";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef, useState } from "react";

const CLASSIFICATION_COLORS: Record<string, string> = {
  Target: "#10b981",
  High: "#f59e0b",
  Low: "#f59e0b",
  "Very High": "#ef4444",
  "Very Low": "#ef4444",
  "Critical High": "#dc2626",
  "Critical Low": "#dc2626",
};

const CLASSIFICATION_SEVERITY: Record<string, number> = {
  Target: 0,
  High: 1,
  Low: 1,
  "Very High": 2,
  "Very Low": 2,
  "Critical High": 3,
  "Critical Low": 3,
};

const UNCLASSIFIED_COLOR = "#6b7280";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export interface ChartDataPoint {
  timestamp: number;
  dateLabel: string;
  value: number;
  observationId: string;
  classification: string | undefined;
  color: string;
}

export interface ThresholdLine {
  value: number;
  classification: string;
  color: string;
}

export interface LegendItem {
  classification: string;
  color: string;
  rangeLabel: string;
}

const getThresholdValue = (range: VitalConfigurationRange): number[] => {
  if (range.comparison.type === "Between") {
    return [range.comparison.value.lower, range.comparison.value.upper];
  }
  return [range.comparison.value];
};

export interface UseVitalsChartProps {
  observations: CombinedObservation[] | undefined;
  thresholds: VitalConfiguration[];
  selectedThresholdIds: string[] | undefined;
  observationType: ObservationType;
  observationId: string | undefined;
  setObservationId: Dispatch<SetStateAction<string | undefined>>;
}

export const useVitalsChart = ({
  observations,
  thresholds,
  selectedThresholdIds,
  observationType,
  observationId,
  setObservationId,
}: UseVitalsChartProps) => {
  const availableUnits = useMemo<string[]>(() => {
    if (observationType === "blood-pressure") {
      return ["SBP", "DBP", "BPM"];
    }

    if (!observations?.length) return [];

    const units = observations.reduce<Set<string>>((acc, curr) => {
      curr.measurements.forEach((m) => {
        acc.add(m.unit);
      });
      return acc;
    }, new Set<string>());

    return Array.from(units);
  }, [observationType, observations]);

  const [selectedUnit, setSelectedUnit] = useState<string>(() =>
    observationType === "blood-pressure" ? "SBP" : availableUnits[0] ?? ""
  );

  const activeUnit = useMemo(() => {
    if (availableUnits.includes(selectedUnit)) return selectedUnit;
    return availableUnits[0] ?? "";
  }, [availableUnits, selectedUnit]);

  const activeThresholds = useMemo(
    () => (selectedThresholdIds?.length ? thresholds.filter((t) => selectedThresholdIds.includes(t.id)) : thresholds),
    [thresholds, selectedThresholdIds]
  );

  const thresholdLines = useMemo<ThresholdLine[]>(() => {
    const byValue = activeThresholds.reduce<Map<number, ThresholdLine & { severity: number }>>((acc, config) => {
      if (config.unit !== activeUnit) return acc;

      config.ranges.forEach((range) => {
        const color = CLASSIFICATION_COLORS[range.classification] ?? UNCLASSIFIED_COLOR;
        const severity = CLASSIFICATION_SEVERITY[range.classification] ?? 0;

        getThresholdValue(range).forEach((value) => {
          const existing = acc.get(value);
          if (!existing || severity > existing.severity) {
            acc.set(value, { value, classification: range.classification, color, severity });
          }
        });
      });

      return acc;
    }, new Map());

    return Array.from(byValue.values()).map(({ severity: _, ...line }) => line);
  }, [activeThresholds, activeUnit]);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!observations?.length) return [];

    const points = observations.reduce<ChartDataPoint[]>((acc, obs) => {
      const measurement = obs.measurements.find((m) => m.unit === activeUnit);
      if (!measurement) return acc;

      const observationClassifications = obs.classifications.filter(
        (c) => c.observationId === measurement.observationId
      );
      const resolved = resolveClassificationForThresholds(observationClassifications, selectedThresholdIds);
      const classification = resolved?.classification;
      const color = classification ? CLASSIFICATION_COLORS[classification] ?? UNCLASSIFIED_COLOR : UNCLASSIFIED_COLOR;

      acc.push({
        timestamp: new Date(obs.timestamp).getTime(),
        dateLabel: formatDate(obs.timestamp, "MMM D"),
        value: measurement.value,
        observationId: obs.id,
        classification,
        color,
      });

      return acc;
    }, []);

    return points.sort((a, b) => a.timestamp - b.timestamp);
  }, [observations, activeUnit, selectedThresholdIds]);

  const legendItems = useMemo<LegendItem[]>(() => {
    const seen = new Set<string>();

    const ranges = activeThresholds.filter((config) => config.unit === activeUnit).flatMap((config) => config.ranges);

    const items = ranges.reduce<(LegendItem & { sortValue: number })[]>((acc, range) => {
      if (seen.has(range.classification)) return acc;
      seen.add(range.classification);

      const color = CLASSIFICATION_COLORS[range.classification] ?? UNCLASSIFIED_COLOR;
      const { comparison } = range;

      let rangeLabel = "";
      let sortValue = 0;
      if (comparison.type === "Between") {
        rangeLabel = `${comparison.value.lower}-${comparison.value.upper}`;
        sortValue = comparison.value.lower;
      } else if (comparison.type === "Less Than") {
        rangeLabel = `<${comparison.value}`;
        sortValue = comparison.value - 0.5;
      } else if (comparison.type === "Greater Than") {
        rangeLabel = `>${comparison.value}`;
        sortValue = comparison.value + 0.5;
      }
      acc.push({ classification: range.classification, color, rangeLabel, sortValue });

      return acc;
    }, []);

    return items.sort((a, b) => a.sortValue - b.sortValue).map(({ sortValue: _, ...item }) => item);
  }, [activeThresholds, activeUnit]);

  const yDomain = useMemo<[number, number]>(() => {
    const allValues = [...chartData.map((d) => d.value), ...thresholdLines.map((l) => l.value)];

    if (allValues.length === 0) return [0, 100];

    const targetLines = thresholdLines.filter((l) => l.classification === "Target");

    if (targetLines.length >= 2) {
      const targetValues = targetLines.map((l) => l.value);
      const targetMin = Math.min(...targetValues);
      const targetMax = Math.max(...targetValues);
      const targetCenter = (targetMin + targetMax) / 2;

      const dataMin = Math.min(...allValues);
      const dataMax = Math.max(...allValues);
      const maxDistance = Math.max(Math.abs(dataMax - targetCenter), Math.abs(dataMin - targetCenter));
      const padding = maxDistance * 0.15 || 10;

      return [Math.floor(targetCenter - maxDistance - padding), Math.ceil(targetCenter + maxDistance + padding)];
    }

    if (targetLines.length === 1 && targetLines[0]) {
      const targetCenter = targetLines[0].value;
      const dataMin = Math.min(...allValues);
      const dataMax = Math.max(...allValues);
      const maxDistance = Math.max(Math.abs(dataMax - targetCenter), Math.abs(dataMin - targetCenter));
      const padding = maxDistance * 0.15 || 10;

      return [Math.floor(targetCenter - maxDistance - padding), Math.ceil(targetCenter + maxDistance + padding)];
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1 || 10;

    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData, thresholdLines]);

  const xDomain = useMemo<[number, number]>(() => {
    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    if (!first || !last) return [0, 1];
    return [first.timestamp, last.timestamp];
  }, [chartData]);

  const isShortRange = chartData.length >= 2 && xDomain[1] - xDomain[0] < SEVEN_DAYS_MS;

  const isShortRangeRef = useRef(isShortRange);
  isShortRangeRef.current = isShortRange;

  const formatXTickRef = useRef((timestamp: number) => {
    const iso = new Date(timestamp).toISOString();
    if (isShortRangeRef.current) {
      return `${formatDate(iso, "MMM D")} ${formatTime(iso, "hh:mm a")}`;
    }
    return formatDate(iso, "MMM D");
  });
  const formatXTick = formatXTickRef.current;

  const xTicks = useMemo<number[]>(() => {
    if (chartData.length < 2) return [];
    const [min, max] = xDomain;
    const range = max - min;
    if (range <= 0) return [min];

    const maxTicks = 6;
    const count = Math.min(maxTicks, chartData.length);
    const step = range / (count - 1);
    const ticks: number[] = [];
    for (let i = 0; i < count; i++) {
      ticks.push(Math.round(min + step * i));
    }
    return ticks;
  }, [xDomain, chartData]);

  const onDotClick = useCallback(
    (observationId: string) => {
      setObservationId(observationId);
    },
    [setObservationId]
  );

  const selectedObservationTimestamp = useMemo(() => {
    if (!observationId) return undefined;
    return chartData.find((point) => point.observationId === observationId)?.timestamp;
  }, [chartData, observationId]);

  return {
    chartData,
    thresholdLines,
    legendItems,
    yDomain,
    xDomain,
    xTicks,
    formatXTick,
    activeUnit,
    availableUnits,
    selectedUnit,
    setSelectedUnit,
    selectedObservationId: observationId,
    selectedObservationTimestamp,
    onDotClick,
    showUnitSelector: observationType === "blood-pressure",
  };
};
